import React, { useState, useEffect } from 'react';
import { typeArticleService } from '../../../../services/typeArticleService';
import familleService from '../../../../services/familleTypeArticleService';
import { useTheme } from '../../../../contexts/ThemeContext'; // <-- Add this import
import FamilleSelectDropdown from '../../../common/FamilleSelectDropdown';
const AddTypeArticleModal = ({ isOpen, onClose, onSuccess }) => {
  const [formDataState, setFormDataState] = useState({
    designation: '',
    familleId: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [familles, setFamilles] = useState([]);
  const [loadingFamilles, setLoadingFamilles] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Search state for famille dropdown
  const [searchFamille, setSearchFamille] = useState('');
  const { isDarkMode } = useTheme();
  // Fetch familles when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchFamilles();
    }
  }, [isOpen]);

  const fetchFamilles = async () => {
    setLoadingFamilles(true);
    try {
      const result = await familleService.getFamilleTypeArticles(100, 1);
      console.log('Familles API Response:', result);
      
      if (result.success && result.data?.list) {
        setFamilles(result.data.list);
      } else if (result.success && result.data?.data) {
        setFamilles(result.data.data);
      } else {
        console.warn('Unexpected familles response structure:', result);
        setFamilles([]);
      }
    } catch (error) {
      console.error('Error fetching familles:', error);
      setFamilles([]);
    } finally {
      setLoadingFamilles(false);
    }
  };

  // Filter familles based on search
  const getFilteredFamilles = () => {
    if (!searchFamille) return familles;
    return familles.filter(famille => 
      famille.designation.toLowerCase().includes(searchFamille.toLowerCase())
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormDataState(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validation
      if (!formDataState.designation.trim()) {
        throw new Error('La désignation est obligatoire');
      }
      if (!formDataState.familleId) {
        throw new Error('La famille est obligatoire');
      }

      const formData = new FormData();
      formData.append('Designation', formDataState.designation);
      formData.append('FamilleId', formDataState.familleId);
      if (imageFile) formData.append('Piece', imageFile);

      const result = await typeArticleService.createTypeArticle(formData);
      
      if (result.success) {
        onSuccess();
        resetForm();
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormDataState({
      designation: '',
      familleId: ''
    });
    setSearchFamille('');
    setError('');
    setImageFile(null);
    setImagePreview(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Get selected famille for preview
  const getSelectedFamille = () => {
    return familles.find(famille => famille.id.toString() === formDataState.familleId);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className={`rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className={`px-6 py-4 flex-shrink-0 ${isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-primary-600 to-primary-700'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-3">
                <i className="fas fa-plus text-white text-lg"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Nouveau Type d'Article</h2>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-primary-100'} text-sm`}>Ajouter un nouveau type d'article</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          <div className="p-6">
            {/* Error Message */}
            {error && (
              <div className={`mb-6 p-4 rounded-xl border flex items-center ${
              isDarkMode
                ? 'bg-gray-900 border-gray-800 text-gray-200'
                : 'bg-red-50 border-red-200 text-red-700'
            }`}>
                <i className={`fas fa-exclamation-circle mr-3 ${isDarkMode ? 'text-gray-400' : 'text-red-500'}`}></i>
                <div>
                  <div className="font-medium">Erreur de validation</div>
                  <div className="text-sm">{error}</div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-center mb-4">
                  <div className={`p-2 rounded-lg mr-3 ${isDarkMode ? 'bg-gray-800' : 'bg-blue-100'}`}>
                    <i className={`fas fa-info-circle ${isDarkMode ? 'text-gray-400' : 'text-blue-600'}`}></i>
                  </div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Informations du type d'article</h3>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-tag mr-2 text-gray-400"></i>
                      Désignation *
                    </label>
                    <input
                      type="text"
                      name="designation"
                      value={formDataState.designation}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-primary-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`}
                      placeholder="Ex: DN 25, CO2, Poudre, etc."
                      required
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-folder mr-2 text-gray-400"></i>
                      Famille *
                    </label>
                    <FamilleSelectDropdown
                      value={formDataState.familleId}
                      onChange={val => setFormDataState(prev => ({ ...prev, familleId: val }))}
                      options={familles}
                      loading={loadingFamilles}
                      disabled={loadingFamilles}
                      isDarkMode={isDarkMode}
                      required={true}
                      placeholder="Sélectionner une famille"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-image mr-2 text-gray-400"></i>
                      Image du type d'article
                    </label>
                    <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${isDarkMode ? 'border-gray-600 hover:border-primary-400' : 'border-gray-300 hover:border-primary-400'}`}>
                      <input
                        type="file"
                        accept="image/*"
                        id="type-article-image-upload"
                        onChange={e => {
                          const file = e.target.files[0];
                          setImageFile(file);
                          setImagePreview(file ? URL.createObjectURL(file) : null);
                        }}
                        className="hidden"
                      />
                      <label htmlFor="type-article-image-upload" className="cursor-pointer">
                        {imageFile ? (
                          <div className="flex flex-col items-center justify-center">
                            <img
                              src={imagePreview}
                              alt="Aperçu"
                              className="h-24 w-auto rounded shadow border mb-2 object-contain"
                            />
                            <div className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{imageFile.name}</div>
                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {(imageFile.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                            <button
                              type="button"
                              className={`mt-2 text-xs hover:underline ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                              onClick={e => {
                                e.preventDefault();
                                setImageFile(null);
                                setImagePreview(null);
                              }}
                            >
                              Supprimer l'image
                            </button>
                          </div>
                        ) : (
                          <div>
                            <i className={`fas fa-cloud-upload-alt text-3xl mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}></i>
                            <p className={`mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Cliquez pour sélectionner une image</p>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>JPG, PNG (Max 5MB)</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview */}
              {formDataState.designation && formDataState.familleId && (
                <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-primary-50 border-primary-200'}`}>
                  <div className="flex items-center mb-4">
                    <div className={`p-2 rounded-lg mr-3 ${isDarkMode ? 'bg-gray-800' : 'bg-primary-100'}`}>
                      <i className={`fas fa-eye ${isDarkMode ? 'text-gray-400' : 'text-primary-600'}`}></i>
                    </div>
                    <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Aperçu</h3>
                  </div>
                  <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-primary-200'}`}>
                    <div className="flex items-center">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${isDarkMode ? 'bg-gray-700 text-gray-200 border-gray-600' : 'bg-blue-100 text-blue-600'}`}>
                        <i className="fas fa-layer-group text-lg"></i>
                      </div>
                      <div>
                        <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formDataState.designation}</h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {getSelectedFamille()?.designation || 'Famille non trouvée'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className={`border-t px-6 py-4 flex-shrink-0 ${isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'}`}>
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className={`px-6 py-3 rounded-lg transition-colors font-medium ${
              isDarkMode
                ? 'text-gray-200 bg-gray-700 border border-gray-600 hover:bg-gray-600'
                : 'text-gray-600 bg-white border border-gray-300 hover:bg-gray-50'
            }`}
            >
              <i className="fas fa-times mr-2"></i>
              Annuler
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading}
              className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : isDarkMode
                  ? 'bg-gray-600 hover:bg-gray-700 text-white shadow-lg hover:shadow-xl'
                  : 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl'
              }`}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Création...
                </div>
              ) : (
                <div className="flex items-center">
                  <i className="fas fa-save mr-2"></i>
                  Créer le type d'article
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddTypeArticleModal;